import { IconAlertCircle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { useImperativeHandle } from 'react';
import { Control, Controller, useFormState, useWatch } from 'react-hook-form';

import { Loader } from '@/ui-lib/loader';

import { previewSubscriptionChange, previewSubscriptionCosts } from '../../../_components/api';
import { SelectedReportsFormData } from '../../../_components/CheckoutForm';
import { MarketReportGroupVM } from '../../../_components/types';
import { calculateTotal, extractIdFromKey, getFilteredItemsForCheckout } from './totals-calc';

export const TotalsRow = ({
    isManagingSubscription,
    control,

    reports,

    initialSelectedPrices,
    queryRef,
}: {
    isManagingSubscription: boolean;

    initialSelectedPrices: SelectedReportsFormData['selectedPrices'];

    control: Control<SelectedReportsFormData>;

    reports: MarketReportGroupVM[];

    queryRef: React.Ref<{
        refetchTotal: () => void;
    }>;
}) => {
    // Watch the selected prices to trigger tax preview updates
    const selectedPrices = useWatch({ control, name: 'selectedPrices' });
    const items = getFilteredItemsForCheckout(selectedPrices, reports);

    // Fetch tax preview for new subscriptions
    const {
        data: costsData,
        isLoading: isLoadingCosts,
        isRefetching: isRefetchingCosts,
        refetch: refetchCosts,
    } = useQuery({
        queryKey: ['subscription-costs-preview', selectedPrices],
        queryFn: () => previewSubscriptionCosts(items),
        enabled: items.length > 0,
        staleTime: 30000, // Cache for 30 seconds
    });

    useImperativeHandle(queryRef, () => ({
        refetchTotal: () => {
            // Trigger a refetch of the total costs
            refetchCosts();
        },
    }));

    return (
        <Controller
            control={control}
            name="selectedPrices"
            render={({ field }) => {
                if (Object.keys(field.value).length === 0) {
                    return <></>;
                }

                let currency = 'EUR';

                // Get currency from first selected item
                const firstSelected = Object.keys(field.value)[0];
                if (firstSelected) {
                    const firstSelectedItem = field.value[firstSelected];

                    if (firstSelectedItem.type === 'group') {
                        const id = extractIdFromKey(firstSelected);

                        const report = reports.find((r) => `${r.id}` === id);
                        currency = report?.price?.currency || 'EUR';
                    }
                }

                // Calculate total
                const { total } = calculateTotal(field.value, initialSelectedPrices, reports);

                const taxAmount = costsData?.tax;
                const taxDescription = costsData?.taxDescription ?? 'Tax';
                const totalWithTax = costsData?.total ?? total;

                return (
                    <>
                        <div className="flex items-center justify-between pt-4 border-neutral-700 mt-1! text-base text-neutral-300">
                            <div className="flex flex-col">
                                <p>Subtotal</p>
                            </div>
                            <p>
                                {currency === 'EUR' ? '€' : '$'}
                                {Math.abs(total).toFixed(2)} / month
                            </p>
                        </div>

                        <div className="flex items-center justify-between text-base text-neutral-300">
                            <p className="">{taxDescription}</p>
                            {isLoadingCosts ? (
                                <Loader mode="light" size="sm" />
                            ) : (
                                <p
                                    className={classNames('', {
                                        'animate-pulse': isRefetchingCosts,
                                    })}
                                >
                                    {typeof taxAmount === 'number' && !isNaN(taxAmount) ? (
                                        <>
                                            {currency === 'EUR' ? '€' : '$'}
                                            {Math.abs(taxAmount).toFixed(2)}
                                        </>
                                    ) : (
                                        '-'
                                    )}
                                </p>
                            )}
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-neutral-700 mt-8">
                            <p className="text-base font-semibold text-neutral-100">Total</p>
                            <p className="text-base font-semibold text-neutral-100">
                                {currency === 'EUR' ? '€' : '$'}
                                {Math.abs(totalWithTax).toFixed(2)} / month
                            </p>
                        </div>
                    </>
                );
            }}
        />
    );
};

export const PreviewRow = ({
    subscriptionId,
    control,
    reports,
}: {
    subscriptionId: number;
    control: Control<SelectedReportsFormData>;
    reports: MarketReportGroupVM[];
}) => {
    // Watch the selected prices to trigger preview updates
    const selectedPrices = useWatch({ control, name: 'selectedPrices' });

    const items = getFilteredItemsForCheckout(selectedPrices, reports);

    // no need to load preview if form is not dirty
    const isDirty = useFormState({ control }).isDirty;

    // Fetch preview data
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['subscription-preview', subscriptionId, selectedPrices],
        queryFn: () => previewSubscriptionChange(subscriptionId, items),
        enabled: !!subscriptionId && items.length > 0 && isDirty,
        staleTime: 30000, // Cache for 30 seconds
    });

    if (items.length === 0 || !isDirty) {
        return null;
    }

    if (isError) {
        return (
            <div className="pt-4 border-t border-neutral-700">
                <div className="flex items-center gap-2 text-orange-400 text-sm">
                    <IconAlertCircle className="w-4 h-4" />
                    <p>Unable to load preview: {error instanceof Error ? error.message : 'Unknown error'}</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-between pt-4 border-t border-neutral-700">
                <p className="text-base font-semibold text-neutral-100">Loading preview...</p>
                <Loader mode="light" size="sm" />
            </div>
        );
    }

    if (!data) {
        return null;
    }

    const currency = data.currency.toLowerCase() === 'eur' ? '€' : '$';

    const isCredit = data.immediateCharge < 0;

    // Get tax from the API response
    const immediateTax = data.immediateTax ?? 0;
    const taxDescription = data.immediateTaxDescription || 'Tax';
    const nextMonthlyTax = data.nextMonthlyTax ?? 0;
    const nextMonthlyTotal = data.nextMonthlyTotal ?? data.newPrice;

    return (
        <>
            <div className="flex items-center justify-between pt-4 border-t border-neutral-700">
                <div className="flex flex-col">
                    <p className="text-base font-semibold text-neutral-100">
                        {isCredit ? 'Immediate Credit' : 'Immediate Charge'}
                    </p>
                    <p className="text-xs text-neutral-400">
                        {isCredit ? 'Prorated refund applied now' : 'Prorated amount due now'}
                    </p>
                </div>
                <p className={`text-base font-semibold ${isCredit ? 'text-green-400' : 'text-blue-400'}`}>
                    {isCredit ? '-' : ''}
                    {currency}
                    {Math.abs(data.immediateCharge).toFixed(2)}
                </p>
            </div>
            {immediateTax !== 0 && (
                <div className="flex items-center justify-between pt-1">
                    <p className="text-sm text-neutral-400">+{taxDescription}</p>
                    <p className="text-sm text-neutral-400">
                        {currency}
                        {Math.abs(immediateTax).toFixed(2)}
                    </p>
                </div>
            )}
            <div className="flex items-center justify-between pt-4 border-t border-neutral-700 mt-2">
                <div className="flex flex-col">
                    <p className="text-base text-neutral-300">Subtotal</p>
                    <p className="text-xs text-neutral-400">Next billing cycle</p>
                </div>
                <p className="text-base font-semibold text-neutral-100">
                    {currency}
                    {Math.abs(data.newPrice).toFixed(2)}
                </p>
            </div>
            {nextMonthlyTax !== 0 && (
                <div className="flex items-center justify-between pt-1">
                    <p className="text-sm text-neutral-400">{taxDescription}</p>
                    <p className="text-sm text-neutral-400">
                        {currency}
                        {Math.abs(nextMonthlyTax).toFixed(2)}
                    </p>
                </div>
            )}
            <div className="flex items-center justify-between pt-2">
                <div className="flex flex-col">
                    <p className="text-base font-semibold text-neutral-100">Next Monthly Bill</p>
                    <p className="text-xs text-neutral-400">Total with tax</p>
                </div>
                <p className="text-base font-semibold text-neutral-100">
                    {currency}
                    {Math.abs(nextMonthlyTotal).toFixed(2)}
                </p>
            </div>
        </>
    );
};
